import React, { useEffect, useMemo, useRef, useState } from 'react';
import cx from 'classnames';

import { getFontFamilies, loadFontFace, processFontFamily, TAILWIND_CSS_CLASS } from './theme/themeHelpers';
import { resolveThemeBlockCss, type PageThemeCss } from './theme/themeBlockValidation';
import { captureFrontendOperationalEvent } from '@mutiny-pkg/telemetry';
import { Helmet } from 'react-helmet';
import { PageTreeNode } from '@mutiny-pkg/champaign-schemas/src/schemas/flexiblePages/flexiblePagesTreeNode';
import '@tailwindplus/elements';
import { FlexiblePagePortalProvider } from './FlexiblePagePortalContext';

interface PageProps {
  children: React.ReactNode;
  treeNode: PageTreeNode;
  editorClassName?: string;
  skipFontLoading?: boolean;
  staticAssetsBaseURL?: string | null;
}

type PageDivProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'children'>;

export const Page = React.forwardRef<HTMLDivElement, PageProps>(
  (
    {
      children,
      treeNode,
      editorClassName,
      skipFontLoading,
      staticAssetsBaseURL,
      ...divProps
    }: PageProps & PageDivProps,
    ref,
  ) => {
    const { pageTitle, favicon, pageDescription, ...themeProps } = treeNode.data;

    const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(null);
    const lastKnownGoodThemeCssRef = useRef<PageThemeCss | null>(null);
    const lastThemeFailureSignatureRef = useRef<string | null>(null);

    useEffect(() => {
      if (skipFontLoading) return;

      const fontFaces = processFontFamily(getFontFamilies(treeNode.data), staticAssetsBaseURL);

      fontFaces.forEach((font) => {
        void loadFontFace(font.fontFamily, font.fontWeight, font.src, font.fontStyle);
      });
    }, [treeNode, skipFontLoading, staticAssetsBaseURL]);

    // Validate the generated theme block before it reaches the canvas runtime: @tailwindcss/browser
    // compiles the style element as one block, so a single unknown utility would strip every theme
    // utility. On failure, keep the last-known-good block (or the minimal fallback on a first
    // render) and report an operational event instead of letting the block fail in the runtime.
    const themeCss = useMemo(() => resolveThemeBlockCss(themeProps, lastKnownGoodThemeCssRef.current), [themeProps]);

    useEffect(() => {
      if (!themeCss.failure) {
        lastKnownGoodThemeCssRef.current = themeCss.content;
        lastThemeFailureSignatureRef.current = null;
        return;
      }
      // themeProps is recreated on every Page render, so without a signature the same failure
      // would re-emit the operational event on each rerender.
      const signature = `${treeNode.id}|${themeCss.failure.reason}|${themeCss.failure.offendingClasses.join(',')}`;
      if (lastThemeFailureSignatureRef.current === signature) return;
      lastThemeFailureSignatureRef.current = signature;
      captureFrontendOperationalEvent('editor.tailwind_theme_failed', {
        offendingClasses: themeCss.failure.offendingClasses,
        themeId: treeNode.id,
        reason: themeCss.failure.reason,
      });
    }, [themeCss, treeNode.id]);

    return (
      <>
        <Helmet>
          <style type="text/tailwindcss">{`
            @theme inline {
              ${themeCss.content.inlineThemeVars.join('\n              ')}
            }
            ${themeCss.content.components}

            /* Avoid tailwind normalizing font weights, use browser defaults for standard handling */
            b, strong {
              font-weight: 700;
            }
          `}</style>
        </Helmet>

        <main>
          <FlexiblePagePortalProvider container={portalContainer}>
            <div
              {...divProps}
              ref={ref}
              data-flexible-page-root
              style={{
                ...(divProps.style as React.CSSProperties),
                ...(themeCss.content.scopedVariables as React.CSSProperties),
              }}
              className={cx(TAILWIND_CSS_CLASS, editorClassName, divProps.className)}
            >
              {children}
              <div ref={setPortalContainer} data-flexible-page-modal-portal-root />
            </div>
          </FlexiblePagePortalProvider>
        </main>
      </>
    );
  },
);

Page.displayName = 'Page';
